import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';

import { ListParameter } from '@models/common';
import { MyDriveFileModel } from '@models/files/mydrive-file.model';
import { GenerateZipTimeEstimate } from '@models/files/zip-estimate.model';
import { ListResponse } from '@models/helpers';
import { BaseRestrictions } from '@models/module/fields/enums';
import { AttachmentModel, FileChunkParams, FileItemModel, SearchFileModel } from '@models/module/fields/main';
import {
  DriveTableAction,
  DriveTableActionsEnum,
  DriveTableRow,
} from '@module-instance/my-drive/common/models/data-list.models';
import {
  AddChunkedVersionParams,
  CreateFolderParams,
  DeleteItemsModel,
  DeleteSharesParams,
  DuplicateVersionBody,
  DuplicateVersionParams,
  EditFolderParams,
  FolderParentModel,
  GetFolderParams,
  GetFolderParentsParams,
  GetUploadUrlParams,
  GetVersionUrlParams,
  MoveItemsModel,
  RenameFileParams,
  RestoreItemsModel,
  SaveFileInfoBody,
  SaveFileInfoParams,
  ShareFilesModel,
  ZipItemsModel,
} from '@module-instance/my-drive/common/models/request.models';
import { IFileService } from '@module-instance/my-drive/common/services/file-factory.service';
import { parseZipDownloadResponse } from '@module-instance/my-drive/common/utils/zip-download.utils';
import { DisplayFormats, TableColumn } from '@shared/components/data-list/common/models/table.interface';
import { FILE_SIZES } from '@shared/consts';
import { HttpService } from '@shared/services';
import { environment } from 'src/environments/environment';

/* TODO: move it in module/instance/services */
@Injectable({ providedIn: 'root' })
export class MyDriveService implements IFileService<MyDriveFileModel> {
  httpService = inject(HttpService);
  private http = inject(HttpClient);

  getFileUrl(fileId: number, versionId: number, isPreview?: boolean, streaming?: boolean) {
    const params = this.httpService.buildHttpParams({
      fileId,
      versionId,
      ...(isPreview ? { isPreview } : {}),
      ...(streaming ? { streaming } : {}),
    });
    return this.httpService
      .get('_api/FolderFile/GetFileURL', {
        httpOptions: { params },
      })
      .pipe(map((response: { url: string }) => response?.url));
  }

  getFilesUrl(versionIds: number[]) {
    return this.httpService.put<{ versionId: number; url: string }[]>('_api/FolderFile/GetFilesURL', {
      files: versionIds.map(versionId => ({ versionId })),
    });
  }

  getFile(
    fileId: number,
    parentId: number,
    entityInstanceId: number,
    versionId?: number,
  ): Observable<MyDriveFileModel> {
    const params = this.httpService.buildHttpParams({ fileId, parentId, entityInstanceId, versionId });
    return this.httpService.get<MyDriveFileModel>(`_api/FolderFile/GetFile`, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  getFolderParents({ folderId }: GetFolderParentsParams): Observable<FolderParentModel[]> {
    const params = this.httpService.buildHttpParams({ folderId });
    return this.httpService.get<FolderParentModel[]>('_api/FolderFile/GetFolderParentsList', {
      useLoader: false,
      httpOptions: { params },
    });
  }

  searchFiles(props: SearchFileModel) {
    return this.httpService.post<ListResponse<FileItemModel>>('_api/FolderFile/Search', props, { useLoader: false });
  }

  getFileUploadUrl(body: GetUploadUrlParams) {
    const params = this.httpService.buildHttpParams({
      parentId: body.parentId,
      mimeType: body.mimeType,
      ...(body.isCreateVersion != null ? { isCreateVersion: body.isCreateVersion } : {}),
    });
    return environment.baseApiUrl + '_api/FolderFile/UploadFile?' + params.toString();
  }

  getFileNewVersionUrl(body: GetVersionUrlParams) {
    const params = this.httpService.buildHttpParams({
      fileId: body.fileId,
      mimeType: body.mimeType,
    });
    return environment.baseApiUrl + '_api/FolderFile/AddVersion?' + params.toString();
  }

  generateFileId() {
    return this.httpService
      .get<{ physicalFileId: string }>('_api/FolderFile/GenerateFileId', { useLoader: false })
      .pipe(map(response => response.physicalFileId));
  }

  uploadChunk(fileId: string, formData: FormData): Observable<void> {
    return this.httpService.post(`_api/FolderFile/UploadChunk?fileId=${fileId}`, formData, {
      useLoader: false,
      useErrorNotification: false,
    });
  }

  saveFileInfo(params: SaveFileInfoParams, body: SaveFileInfoBody) {
    const httpParams = this.httpService.buildHttpParams(params);
    return this.httpService.post<FileItemModel>('_api/FolderFile/SaveFileInfo', body, {
      useLoader: false,
      httpOptions: { params: httpParams },
    });
  }

  addChunkedVersion(params: AddChunkedVersionParams, body: SaveFileInfoBody) {
    const httpParams = this.httpService.buildHttpParams(params);
    return this.httpService.post<FileItemModel>('_api/ProjectFolderFile/AddChunkedVersion', body, {
      useLoader: false,
      httpOptions: { params: httpParams },
    });
  }

  renameFile(body: RenameFileParams): Observable<void> {
    return this.httpService.put(`_api/FolderFile/RenameFile`, body, { useLoader: false });
  }

  createFolder(body: CreateFolderParams): Observable<void> {
    return this.httpService.post(`_api/FolderFile/CreateFolder`, body, { useLoader: false });
  }

  getFolderId(body: GetFolderParams): Observable<number> {
    return this.httpService.post<number>(`_api/FolderFile/GetFolderId`, body, { useLoader: false });
  }

  uploadFiles(files: File[], parentId: number, mimeType: string): Observable<AttachmentModel[]> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file, file.name));

    const params = this.httpService.buildHttpParams({ parentId, mimeType });

    return this.httpService.post<AttachmentModel[]>(`_api/FolderFile/UploadFiles`, formData, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  editFolder(body: EditFolderParams): Observable<void> {
    return this.httpService.put(`_api/FolderFile/EditFolder`, body, { useLoader: false });
  }

  downloadFileFromUrl(url: string, filename?: string) {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'file';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading file from url', url, e);
    }
  }

  getChunkData({ file, start, end, chunks }: FileChunkParams) {
    const chunk = file.slice(start, end);
    const formData = new FormData();
    formData.append('file', chunk, file.name);
    formData.append('chunks', chunks.toString());
    formData.append('fileName', file.name);
    formData.append('_chunkSize', FILE_SIZES.chunkSize.toString());
    formData.append('_currentChunkSize', (end - start).toString());
    formData.append('_chunkNumber', Math.ceil(start / FILE_SIZES.chunkSize).toString());
    formData.append('_totalSize', file.size.toString());

    return formData;
  }

  duplicateVersion(params: DuplicateVersionParams, body: DuplicateVersionBody) {
    const httpParams = this.httpService.buildHttpParams(params);
    return this.httpService.post<number>('_api/FolderFile/DuplicateVersion', body, {
      useLoader: false,
      httpOptions: { params: httpParams },
    });
  }

  deleteSharesGlobal(body: ListParameter, parentId: number): Observable<void> {
    return this.httpService.put(`_api/FolderFile/DeleteSharesGlobal?parentId=${parentId}`, body, { useLoader: false });
  }

  deleteShares(body: DeleteSharesParams): Observable<void> {
    return this.httpService.put(`_api/FolderFile/DeleteShares`, body, { useLoader: false });
  }

  saveFile(body: Partial<MyDriveFileModel>): Observable<void> {
    return this.httpService.put(`_api/FolderFile/SaveFile`, body, { useLoader: false });
  }

  deleteItems(body: DeleteItemsModel): Observable<void> {
    return this.httpService.put(`_api/FolderFile/Delete`, body, { useLoader: false });
  }

  permanentlyDeleteItems(body: DeleteItemsModel): Observable<void> {
    return this.httpService.put(`_api/FolderFile/PermanentlyDelete`, body, { useLoader: false });
  }

  restoreItems(body: RestoreItemsModel): Observable<void> {
    return this.httpService.put(`_api/FolderFile/Restore`, body, { useLoader: false });
  }

  moveItems(body: MoveItemsModel): Observable<void> {
    return this.httpService.put(`_api/FolderFile/Move`, body);
  }

  shareFiles(body: ShareFilesModel): Observable<void> {
    return this.httpService.put(`_api/FolderFile/ShareFile`, body, { useLoader: false });
  }

  setFileAsUserAvatar(fileId: number): Observable<void> {
    return this.httpService.put(`_api/FolderFile/SetFileAsUserLogo?fileId=${fileId}`, null, { useLoader: false });
  }

  setFileAsCompanyLogo(fileId: number): Observable<void> {
    return this.httpService.put(`_api/FolderFile/SetFileAsCompanyLogo?fileId=${fileId}`, null, { useLoader: false });
  }

  downloadZip(body: ZipItemsModel): Observable<HttpResponse<Blob> | GenerateZipTimeEstimate> {
    return this.http
      .put(environment.baseApiUrl + '_api/FolderFile/DownloadZipFromFolderAndFiles', body, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(switchMap(parseZipDownloadResponse));
  }

  getTableColumns(): TableColumn[] {
    return [
      {
        id: 0,
        name: 'image',
        displayName: '',
        srOnlyLabel: 'a11y.fileExplorer.columnPreview',
        priority: 3,
        sortable: false,
        width: 0.5,
        align: 'center',
        displayFormatId: DisplayFormats.Image,
      },
      {
        id: 1,
        name: 'name',
        displayName: 'fileExplorer.name',
        priority: 3,
        sortable: true,
        width: 3,
      },
      {
        id: 2,
        name: 'createdBy',
        displayName: 'fileExplorer.createdBy',
        priority: 3,
        sortable: true,
        width: 1,
      },
      {
        id: 3,
        name: 'createdOn',
        displayName: 'fileExplorer.createdOn',
        priority: 2,
        sortable: true,
        displayFormatId: DisplayFormats.Date,
        restrictions: BaseRestrictions.DateTime,
        width: 1,
      },
      {
        id: 4,
        name: 'updatedOn',
        displayName: 'fileExplorer.updatedOn',
        priority: 0,
        sortable: true,
        displayFormatId: DisplayFormats.Date,
        restrictions: BaseRestrictions.DateTime,
        width: 1,
      },
      {
        id: 5,
        name: 'fileSizeDisplay',
        displayName: 'fileDialog.fileSize',
        priority: 1,
        width: 1,
        sortable: true,
      },
    ];
  }

  /** My Drive has no global-search toggle, so the contract column legacy declares here never applies. */
  getSearchColumns(): TableColumn[] {
    const columns = this.getTableColumns();
    columns.splice(columns.findIndex(column => column.name === 'name') + 1, 0, {
      id: columns.length,
      name: 'folderPath',
      displayName: 'fileExplorer.folder',
      priority: 3,
      sortable: false,
      width: 2,
    });
    return columns;
  }

  getRecycleBinColumns(): TableColumn[] {
    return [
      {
        id: 0,
        name: 'image',
        displayName: '',
        srOnlyLabel: 'a11y.fileExplorer.columnPreview',
        priority: 3,
        sortable: false,
        width: 0.5,
        align: 'center',
        displayFormatId: DisplayFormats.Image,
      },
      {
        id: 1,
        name: 'name',
        displayName: 'fileExplorer.name',
        priority: 3,
        sortable: true,
        width: 3,
      },
      {
        id: 2,
        name: 'updatedBy',
        displayName: 'fileExplorer.deletedBy',
        priority: 3,
        sortable: true,
        width: 1,
      },
      {
        id: 3,
        name: 'updatedOn',
        displayName: 'fileExplorer.deletedOn',
        priority: 2,
        sortable: true,
        displayFormatId: DisplayFormats.Date,
        restrictions: BaseRestrictions.DateTime,
        width: 1,
      },
      {
        id: 4,
        name: 'pathWhenDeleted',
        displayName: 'fileExplorer.originalLocation',
        priority: 2,
        sortable: false,
        width: 2,
      },
      {
        id: 5,
        name: 'fileSizeDisplay',
        displayName: 'fileDialog.fileSize',
        priority: 1,
        width: 1,
        sortable: true,
      },
    ];
  }

  getTableActions(): DriveTableAction[] {
    return [
      {
        id: DriveTableActionsEnum.VIEW,
        displayName: 'fileExplorer.view',
        rightIcon: 'start',
        condition: (row: DriveTableRow) => row.isFolder,
      },
      {
        id: DriveTableActionsEnum.OPEN_TAB,
        displayName: 'cipoFields.openNewTab',
        rightIcon: 'open_in_new',
        condition: (row: DriveTableRow) => !row.isFolder,
      },
      {
        id: DriveTableActionsEnum.RENAME,
        displayName: 'fileExplorer.rename',
        rightIcon: 'edit',
        condition: (row: DriveTableRow) => !row.isLocked && row.id >= 0,
      },
      {
        id: DriveTableActionsEnum.NOTE,
        displayName: 'fileExplorer.note',
        rightIcon: 'sticky_note_2',
        condition: (row: DriveTableRow) => !row.isFolder && row.id >= 0,
      },
    ];
  }
}
